import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as apiLogin, register as apiRegister, logout as apiLogout, getCurrentUser } from '../services/api';

const AuthContext = createContext();

export const computeLevel = (points = 0) => {
  const derived = Math.floor(Math.sqrt(points / 10));
  return derived > 0 ? derived : 1;
};

export const computeBadge = (points = 0) => {
  if (points >= 10000) return 'Ultimate Task Destroyer of the Universe';
  if (points >= 5000) return 'Task Dominator';
  if (points >= 1000) return 'Master';
  if (points >= 500) return 'Task Pro';
  if (points >= 100) return "Do'er";
  return 'Starter';
};

const normalizeUser = (user) => {
  if (!user) return null;
  const totalPoints = user.total_points ?? user.totalPoints ?? 0;
  const displayName = user.display_name ?? user.displayName ?? user.username;
  const level = computeLevel(totalPoints);
  const twoFactorEnabled = user.two_factor_enabled ?? false;
  return {
    ...user,
    total_points: totalPoints,
    display_name: displayName,
    two_factor_enabled: twoFactorEnabled,
    level,
    badge: computeBadge(totalPoints),
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await getCurrentUser();
          setUser(normalizeUser(response.user));
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const register = async (userData) => {
    try {
      setError(null);
      const response = await apiRegister(userData);
      localStorage.setItem('token', response.token);
      setUser(normalizeUser(response.user));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      setError(message);
      return { success: false, message };
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await apiLogin(credentials);
      localStorage.setItem('token', response.token);
      setUser(normalizeUser(response.user));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      setError(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    apiLogout();
    setUser(null);
    setError(null);
  };

  const updateUser = (updatedData) => {
    setUser(prevUser => normalizeUser({ ...prevUser, ...updatedData }));
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
